import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Alert,
} from '@mui/material';

export interface ShareSettings {
  id?: string;
  password?: string;
  expiryDate?: Date | null;
  allowDownload: boolean;
  filePath: string;
  fileName: string;
  createdAt: Date;
  downloadCount: number;
  maxDownloads?: number;
}

interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  filePath: string;
  onShare: (settings: ShareSettings) => Promise<string>;
}

export function ShareDialog({ open, onClose, fileName, filePath, onShare }: ShareDialogProps) {
  const [password, setPassword] = useState('');
  const [expiryDate, setExpiryDate] = useState<Date | null>(null);
  const [allowDownload, setAllowDownload] = useState(true);
  const [showLink, setShowLink] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [maxDownloads, setMaxDownloads] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);

  const handleShare = async () => {
    setLoading(true);
    try {
      const settings: ShareSettings = {
        password: password || undefined,
        expiryDate,
        allowDownload,
        filePath,
        fileName,
        createdAt: new Date(),
        downloadCount: 0,
        maxDownloads
      };

      const shareId = await onShare(settings);
      
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/share/${shareId}`;
      setShareLink(link);
      setShowLink(true);
    } catch (error) {
      console.error('Error sharing file:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>分享文件</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            文件名：{fileName}
          </Typography>
        </Box>
        
        {!showLink ? (
          <>
            <TextField
              margin="dense"
              label="访问密码"
              type="text"
              fullWidth
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              helperText="可选，留空则无需密码"
              sx={{ mb: 2 }}
            />

            <TextField
              type="datetime-local"
              label="过期时间"
              value={expiryDate ? new Date(expiryDate.getTime() - expiryDate.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const date = e.target.value ? new Date(e.target.value) : null;
                setExpiryDate(date);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)
              }}
              fullWidth
              sx={{ mb: 2 }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={allowDownload}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAllowDownload(e.target.checked)}
                />
              }
              label="允许下载"
            />

            <TextField
              type="number"
              label="最大下载次数"
              value={maxDownloads}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxDownloads(parseInt(e.target.value) || undefined)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              sx={{ mt: 2 }}
              helperText="可选，留空则不限制下载次数"
            />
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              文件分享成功！
            </Alert>
            <TextField
              fullWidth
              value={shareLink}
              InputProps={{
                readOnly: true,
              }}
              helperText="点击复制链接"
              onClick={() => {
                navigator.clipboard.writeText(shareLink);
                alert('链接已复制到剪贴板');
              }}
            />
            {password && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                访问密码：{password}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
        {!showLink && <Button onClick={handleShare} disabled={loading} color="primary">创建分享</Button>}
      </DialogActions>
    </Dialog>
  );
}